import React, { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Search, Archive, Calendar, User, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STATUS_META = {
  draft: { label: "Ciornă", cls: "bg-neutral-100 text-neutral-700" },
  pending_substitute: { label: "Confirmare înlocuitor", cls: "bg-violet-100 text-violet-700" },
  substitute_rejected: { label: "Înlocuitor respins", cls: "bg-rose-100 text-rose-700" },
  pending_manager: { label: "Așteaptă Manager", cls: "bg-amber-100 text-amber-700" },
  pending_hr: { label: "Așteaptă HR", cls: "bg-blue-100 text-blue-700" },
  approved: { label: "Aprobată", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Respinsă", cls: "bg-red-100 text-red-700" },
  withdrawn: { label: "Retrasă", cls: "bg-neutral-100 text-neutral-600" },
  cancelled: { label: "Anulată", cls: "bg-neutral-100 text-neutral-600" },
  pending_cancellation: { label: "Anulare în așteptare", cls: "bg-orange-100 text-orange-700" },
};

function fmtDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return "-";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, cls: "bg-neutral-100 text-neutral-700" };
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${m.cls}`}>{m.label}</span>;
}

export function TimeOffRecordsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState("all");
  const [status, setStatus] = useState("all");
  const [employee, setEmployee] = useState("all");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (year !== "all") params.year = year;
      if (status !== "all") params.status = status;
      const { data } = await api.get("/timeoff/records", { params });
      setItems(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [year, status]);

  async function deleteRequest(r) {
    if (!window.confirm("Ștergi definitiv această cerere? Zilele de concediu revin automat la cele disponibile.")) return;
    setDeleting(true);
    try {
      await api.delete(`/timeoff/requests/${r.id}`);
      toast.success("Cerere ștearsă — zilele au revenit la cele disponibile");
      setDetail(null);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setDeleting(false); }
  }

  const employees = useMemo(() => {
    const map = {};
    for (const r of items) if (r.employee?.id) map[r.employee.id] = r.employee.name;
    return Object.entries(map).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  const years = useMemo(() => {
    const s = new Set();
    for (const r of items) if (r.start_date) s.add(r.start_date.slice(0, 4));
    s.add(String(new Date().getFullYear()));
    return Array.from(s).sort().reverse();
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter(r => {
      if (employee !== "all" && r.employee?.id !== employee) return false;
      if (q.trim()) {
        const hay = `${r.employee?.name || ""} ${r.type?.name || ""} ${r.reason || ""}`.toLowerCase();
        if (!hay.includes(q.trim().toLowerCase())) return false;
      }
      return true;
    });
  }, [items, employee, q]);

  return (
    <div>
      {/* Filtre */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative w-full sm:w-auto">
          <Search className="h-4 w-4 text-ink-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Caută angajat, tip, motiv..."
            data-testid="records-search"
            className="pl-9 pr-3 py-2 border border-neutral-300 text-sm w-full sm:w-[280px] rounded-md" />
        </div>
        <select value={employee} onChange={e => setEmployee(e.target.value)} data-testid="records-employee"
          className="border border-neutral-300 px-3 py-2 text-sm rounded-md flex-1 sm:flex-none min-w-[120px]">
          <option value="all">Toți angajații</option>
          {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <select value={year} onChange={e => setYear(e.target.value)} data-testid="records-year"
          className="border border-neutral-300 px-3 py-2 text-sm rounded-md flex-1 sm:flex-none min-w-[100px]">
          <option value="all">Toți anii</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={status} onChange={e => setStatus(e.target.value)} data-testid="records-status"
          className="border border-neutral-300 px-3 py-2 text-sm rounded-md flex-1 sm:flex-none min-w-[140px]">
          <option value="all">Toate statusurile</option>
          {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <span className="text-[13px] text-ink-muted ml-auto">{filtered.length} cerere(i)</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white border border-neutral-200 text-ink-muted rounded-lg">
          <Archive className="h-8 w-8 mx-auto text-ink-muted mb-3" />
          <p className="text-sm">Nicio cerere în evidență pentru filtrele selectate.</p>
        </div>
      ) : (
        <>
        <div className="md:hidden" data-testid="records-cards">
          {filtered.map(r => (
            <button key={r.id} onClick={() => setDetail(r)} data-testid={`records-card-${r.id}`} className="m-card w-full text-left active:scale-[0.99] transition-transform">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <div className="font-semibold text-[14.5px] text-ink truncate">{r.employee?.name}</div>
                  <div className="text-[12px] text-ink-muted truncate">{r.employee?.job_title}</div>
                </div>
                <StatusBadge status={r.status} />
              </div>
              <div className="m-kv">
                <span className="m-kv-label">Tip</span>
                <span className="m-kv-value inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: r.type?.color }} />{r.type?.name}</span>
              </div>
              <div className="m-kv">
                <span className="m-kv-label">Perioadă</span>
                <span className="m-kv-value">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</span>
              </div>
              <div className="m-kv">
                <span className="m-kv-label">Zile</span>
                <span className="m-kv-value font-mono font-semibold">{r.business_days}</span>
              </div>
            </button>
          ))}
        </div>
        <div className="surface overflow-x-auto hidden md:block">
          <table className="w-full text-[14px]" data-testid="records-table">
            <thead className="border-b border-neutral-200 bg-neutral-50/50">
              <tr className="text-left">
                <th className="p-4 font-display text-[12px] font-semibold text-ink-muted">Angajat</th>
                <th className="p-4 font-display text-[12px] font-semibold text-ink-muted">Tip</th>
                <th className="p-4 font-display text-[12px] font-semibold text-ink-muted">Perioadă</th>
                <th className="p-4 font-display text-[12px] font-semibold text-ink-muted">Zile</th>
                <th className="p-4 font-display text-[12px] font-semibold text-ink-muted">Status</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/50">
                  <td className="p-4">
                    <div className="font-medium text-ink">{r.employee?.name}</div>
                    <div className="text-[12px] text-ink-muted">{r.employee?.job_title}</div>
                  </td>
                  <td className="p-4">
                    <span className="inline-flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: r.type?.color }} />
                      {r.type?.name}
                    </span>
                  </td>
                  <td className="p-4 text-ink-muted">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</td>
                  <td className="p-4 font-mono text-ink font-semibold">{r.business_days}</td>
                  <td className="p-4"><StatusBadge status={r.status} /></td>
                  <td className="p-4 text-right">
                    <button onClick={() => setDetail(r)} data-testid={`records-detail-${r.id}`}
                      className="text-brand text-[13px] font-semibold hover:underline">Detalii</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      <Dialog open={!!detail} onOpenChange={o => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl flex items-center gap-3">
                  {detail.type?.name} <StatusBadge status={detail.status} />
                </DialogTitle>
                <DialogDescription>
                  <span className="inline-flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {detail.employee?.name}</span>
                  {" · "}
                  <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {fmtDate(detail.start_date)} → {fmtDate(detail.end_date)} ({detail.business_days} zile)</span>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm mt-2">
                {detail.reason && (
                  <div><div className="text-xs uppercase font-mono text-ink-muted mb-1">Motiv</div><div className="text-ink">{detail.reason}</div></div>
                )}
                {detail.substitute && (
                  <div><div className="text-xs uppercase font-mono text-ink-muted mb-1">Înlocuitor</div>
                    <div>{detail.substitute.name} — {detail.substitute.job_title}{detail.substitute_confirmed_at ? " · ✓ confirmat" : ""}</div>
                  </div>
                )}
                {detail.client_situation_label && (
                  <div><div className="text-xs uppercase font-mono text-ink-muted mb-1">Situație client</div><div className="text-ink">{detail.client_situation_label}</div></div>
                )}
                <div>
                  <div className="text-xs uppercase font-mono text-ink-muted mb-2">Istoric</div>
                  <div className="space-y-2">
                    {(detail.history || []).map((h, i) => (
                      <div key={i} className="flex gap-3 border-l-2 border-neutral-200 pl-3 py-1">
                        <div className="text-ink-muted text-xs min-w-[110px]">
                          {`${fmtDate(h.at)}, ${new Date(h.at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}`}
                        </div>
                        <div>
                          <div className="font-medium text-ink">{h.by_name || "-"} — {h.action}</div>
                          {h.comment && <div className="text-ink-muted text-xs mt-0.5">{h.comment}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {isAdmin && (
                <div className="flex justify-end pt-4 mt-2 border-t border-neutral-200">
                  <button onClick={() => deleteRequest(detail)} disabled={deleting}
                    data-testid={`records-delete-${detail.id}`}
                    className="px-4 py-2 text-sm border border-red-300 text-red-600 hover:bg-red-50 inline-flex items-center gap-2">
                    <Trash2 className="h-4 w-4" /> Șterge definitiv
                  </button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function TimeOffRecords() {
  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1400px]">
        <div className="mb-8">
          <p className="font-mono text-[11px] uppercase tracking-widest text-brand mb-2">Aprobări</p>
          <h1 className="font-display font-semibold text-[38px] tracking-[-0.02em] leading-tight text-ink mb-2 flex items-center gap-3">
            <Archive className="h-8 w-8 text-brand" /> Evidență concedii & absențe
          </h1>
          <p className="text-ink-muted text-sm">Istoricul tuturor cererilor de concediu & absențe.</p>
        </div>
        <TimeOffRecordsPanel />
      </div>
    </PortalLayout>
  );
}
